import React, { useState } from "react";
import { Plus, Pencil, Search, X, Printer } from "lucide-react";
import { useSettings } from "../settings/api.js";
import InvoiceModal from "./Invoice.jsx";
import { Chip, EmptyState, Loading, ErrorState, ConfirmButton } from "../ui/primitives.jsx";
import { useToast } from "../ui/Toasts.jsx";
import { fmtMoney, fmtDate } from "../lib/format.js";
import { fmtTime, rentalLiveStatus, lateInfo, rentalDays, useNow } from "../lib/rental-time.js";
import { useRentals, useSetStatus, useDeleteRental, buildCheckIn, buildTogglePaid } from "./api.js";
import RentalForm from "./RentalForm.jsx";

const RENTAL_STATUS = { reserved: "Reserved", out: "On rent", returned: "Returned", cancelled: "Cancelled" };

const FILTERS = [
  ["active", "Active (reserved + on rent)"],
  ["reserved", "Reserved"],
  ["out", "On rent"],
  ["overdue", "Overdue"],
  ["returned", "Returned"],
  ["unpaid", "Unpaid"],
  ["all", "All"],
];

export default function RentalsTab() {
  const [editing, setEditing] = useState(null);
  const [invoicing, setInvoicing] = useState(null);
  const [filter, setFilter] = useState("active");
  const [q, setQ] = useState("");
  const { notify } = useToast();
  useNow(); // overdue labels advance without a reload

  const rentals = useRentals();
  const settings = useSettings();
  const setStatus = useSetStatus();
  const del = useDeleteRental();

  const shown = (rentals.data ?? [])
    .filter((r) => {
      const s = rentalLiveStatus(r);
      if (filter === "active") return s === "reserved" || s === "out" || s === "overdue";
      if (filter === "unpaid") return r.status !== "cancelled" && !r.paid;
      if (filter === "all") return true;
      return s === filter;
    })
    .filter((r) => {
      if (!q) return true;
      const hay = `${r.equipment?.name ?? ""} ${r.customers?.name ?? ""} ${r.customers?.business ?? ""} ${r.invoice_no ?? ""}`;
      return hay.toLowerCase().includes(q.toLowerCase());
    });

  async function onCheckIn(r) {
    const { patch, lateInfo: li } = buildCheckIn(r);
    try {
      await setStatus.mutateAsync({ id: r.id, patch });
      if (li) {
        notify(`Checked in ${li.label} late — review the late fee and final charges.`);
        setEditing({ ...r, ...patch });
      } else {
        notify("Checked in on time.");
      }
    } catch (e) { notify(e.message); }
  }

  return (
    <>
      <div className="card">
        <div className="card-h">
          <h2>Rental agreements</h2>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{ border: "1px solid var(--line)", borderRadius: 6, padding: "7px 10px" }}
          >
            {FILTERS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <div className="searchbox">
            <Search size={15} color="var(--muted)" />
            <input placeholder="Search customer, machine, invoice" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <div className="spacer" />
          <button className="btn primary" onClick={() => setEditing({})}>
            <Plus size={16} /> New rental
          </button>
        </div>

        <div className="card-b tight" style={{ overflowX: "auto" }}>
          {rentals.isPending ? (
            <Loading what="rentals" />
          ) : rentals.error ? (
            <ErrorState error={rentals.error} onRetry={() => rentals.refetch()} />
          ) : shown.length === 0 ? (
            <EmptyState
              title="No rentals here"
              sub={
                (rentals.data ?? []).length === 0
                  ? "Book your first rental to start tracking equipment, dates, and payment."
                  : "Try a different filter or search."
              }
              action={
                (rentals.data ?? []).length === 0 && (
                  <button className="btn primary" onClick={() => setEditing({})}>
                    <Plus size={16} /> New rental
                  </button>
                )
              }
            />
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Equipment</th><th>Customer</th><th>Dates</th><th>Total</th>
                  <th>Status</th><th>Payment</th><th></th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => {
                  const s = rentalLiveStatus(r);
                  const li = lateInfo(r);
                  return (
                    <tr key={r.id}>
                      <td className="main-cell">{r.equipment?.name ?? "—"}</td>
                      <td>
                        <div className="main-cell">{r.customers?.name ?? "—"}</div>
                        {r.customers?.business && <div className="sub-cell">{r.customers.business}</div>}
                      </td>
                      <td>
                        <div>
                          {fmtDate(r.start_date)} {fmtTime(r.start_time)} → {fmtDate(r.end_date)} {fmtTime(r.end_time)}
                        </div>
                        <div className="sub-cell">
                          {rentalDays(r)} day(s)
                          {r.status === "returned" && r.returned_date
                            ? ` · back ${fmtDate(r.returned_date)} ${fmtTime(r.returned_time)}`
                            : ""}
                        </div>
                        {li && (
                          <div style={{ color: "var(--red)", fontWeight: 700, fontSize: 12 }}>
                            {r.status === "returned" ? "Returned " : "Overdue "}
                            {li.label}
                            {r.status === "returned" ? " late" : ""}
                          </div>
                        )}
                      </td>
                      <td className="main-cell">{fmtMoney(r.total)}</td>
                      <td><Chip kind={s}>{s === "overdue" ? "Overdue" : RENTAL_STATUS[r.status]}</Chip></td>
                      <td>
                        <button
                          className="iconbtn"
                          title={r.paid ? "Mark unpaid" : "Mark paid"}
                          style={{ padding: 0 }}
                          onClick={async () => {
                            try { await setStatus.mutateAsync({ id: r.id, patch: buildTogglePaid(r) }); }
                            catch (e) { notify(e.message); }
                          }}
                        >
                          <Chip kind={r.paid ? "paid" : "unpaid"}>{r.paid ? "Paid" : "Unpaid"}</Chip>
                        </button>
                      </td>
                      <td style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                        {r.status === "reserved" && (
                          <button
                            className="btn sm dark"
                            onClick={async () => {
                              try { await setStatus.mutateAsync({ id: r.id, patch: { status: "out" } }); notify("Checked out."); }
                              catch (e) { notify(e.message); }
                            }}
                          >
                            Check out
                          </button>
                        )}{" "}
                        {r.status === "out" && (
                          <button className="btn sm dark" onClick={() => onCheckIn(r)}>Check in</button>
                        )}{" "}
                        <button
                          className="iconbtn"
                          title={r.status === "returned" ? "View / print invoice" : "View / print quote"}
                          onClick={() => setInvoicing(r)}
                        >
                          <Printer size={16} />
                        </button>
                        <button className="iconbtn" title="Edit" onClick={() => setEditing(r)}>
                          <Pencil size={16} />
                        </button>
                        <ConfirmButton
                          className="iconbtn"
                          title="Delete rental"
                          style={{ color: "var(--red)", borderRadius: 5, padding: "5px 6px" }}
                          label={<X size={16} />}
                          confirmLabel="Delete?"
                          onConfirm={async () => {
                            try { await del.mutateAsync(r.id); notify("Rental deleted."); }
                            catch (e) { notify(e.message); }
                          }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {editing !== null && (
        <RentalForm
          rental={editing.id ? editing : null}
          allRentals={rentals.data ?? []}
          onClose={() => setEditing(null)}
        />
      )}
      {invoicing && (
        <InvoiceModal rental={invoicing} settings={settings.data} onClose={() => setInvoicing(null)} />
      )}
    </>
  );
}
